import { textComposerFrameVariantContentJsonSchema } from "./frameOutput";
import type { TextComposerAuthoringState } from "../editor/authoringState";
import { normalizeAuthoringDraft } from "../editor/authoringState";
import type { TextComposerExtension } from "../editor/extensionContract";
import { composeDeserialize } from "../editor/extensionRegistry";
import { defaultExtensions } from "../extensions/defaultExtensions";
import { migrateLegacyProductAliases } from "../extensions/mergeTags/migrateLegacyProductAliases";
import type { ProductGridModelValue } from "@/features/EmailEditor/plugins/productGridConstants";
import type { Frame, FrameVariant } from "@/types/dragon-tiles";

const TEXT_TRANSPORT_MEDIA = ["rcs", "mms", "sms"] as const;

export type TextTransportMedium = (typeof TEXT_TRANSPORT_MEDIA)[number];

export function isTextTransportMedium(
  medium: FrameVariant["medium"],
): medium is TextTransportMedium {
  return medium === "rcs" || medium === "mms" || medium === "sms";
}

/**
 * Picks the first SMS/RCS/MMS variant (priority RCS → MMS → SMS) whose
 * `content_json` is Text Composer–shaped, for seeding the editor.
 */
export function findFirstTextComposerSourceVariant(
  frame: Frame,
): FrameVariant | undefined {
  for (const medium of TEXT_TRANSPORT_MEDIA) {
    const variant = frame.frame_variants.find((v) => v.medium === medium);
    if (
      variant !== undefined &&
      textComposerFrameVariantContentJsonSchema.safeParse(variant.content_json)
        .success
    ) {
      return variant;
    }
  }
  return undefined;
}

/**
 * Seeds the message draft store from a persisted `content_json`. Iterates the
 * registered extensions and lets each pluck its slice from the wire payload.
 * Adding or removing an extension never requires changes to this function.
 *
 * Passing `options.productModel` opts the seed into the legacy V1→V2 product
 * alias migration; callers with no tile-derived model (broadcasts) omit it and
 * legacy aliases keep resolving through the save-time transform. Every parse
 * of the same frame must pass the same model — the dirty-check baseline in the
 * edit route is derived from a second parse of this same `content_json`.
 */
export function parseTextComposerAuthoringSeed(
  contentJson: FrameVariant["content_json"],
  extensions: TextComposerExtension[] = defaultExtensions,
  options?: { productModel?: ProductGridModelValue },
): TextComposerAuthoringState {
  const parsed =
    textComposerFrameVariantContentJsonSchema.safeParse(contentJson);
  if (!parsed.success) {
    return {};
  }

  const wireAuthoring = parsed.data.authoringState as Record<string, unknown>;
  const seed = composeDeserialize(extensions)(wireAuthoring);
  if (
    options?.productModel !== undefined &&
    typeof seed.bodyText === "string"
  ) {
    seed.bodyText = migrateLegacyProductAliases(
      seed.bodyText,
      options.productModel,
    );
  }
  return normalizeAuthoringDraft(seed, extensions);
}

export interface TextComposerFrameSeeds {
  primary: TextComposerAuthoringState;
  fallback?: TextComposerAuthoringState;
}

function findVariantForChannel(
  frame: Frame,
  channel: "primaryChannel" | "fallbackChannel",
): FrameVariant | undefined {
  return frame.frame_variants.find((variant) => {
    const parsed = textComposerFrameVariantContentJsonSchema.safeParse(
      variant.content_json,
    );
    return (
      parsed.success &&
      parsed.data[channel]?.toLowerCase() === variant.medium &&
      isTextTransportMedium(variant.medium)
    );
  });
}

export function parseTextComposerFrameSeeds(
  frame: Frame,
  extensions: TextComposerExtension[] = defaultExtensions,
  options?: { productModel?: ProductGridModelValue },
): TextComposerFrameSeeds {
  const primaryVariant =
    findVariantForChannel(frame, "primaryChannel") ??
    findFirstTextComposerSourceVariant(frame);
  const primary = parseTextComposerAuthoringSeed(
    primaryVariant?.content_json ?? null,
    extensions,
    options,
  );

  const fallbackVariant = findVariantForChannel(frame, "fallbackChannel");
  if (fallbackVariant === undefined || fallbackVariant === primaryVariant) {
    return { primary };
  }

  const parsedFallback = textComposerFrameVariantContentJsonSchema.safeParse(
    fallbackVariant.content_json,
  );
  if (parsedFallback.data?.authoringState.fallbackUnlinked !== true) {
    return { primary };
  }

  return {
    primary,
    fallback: parseTextComposerAuthoringSeed(
      fallbackVariant.content_json,
      extensions,
      options,
    ),
  };
}
